<script setup lang="ts">
import Card from "primevue/card";
import { computed, ref, toRefs } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { useDeleteTodoComment } from "@/components/MyStudents/Submissions/composables/useDeleteTodoComment";
import { useTodoComments } from "@/components/MyStudents/Submissions/composables/useTodoComments";
import LockIcon from "@/components/shared/icons/LockIcon.vue";
import { hasTodoUpdateSubmissionPermission } from "@/composables/authorization.js";
import { ERROR_TIMEOUT } from "@/constants.js";
import CommentComposer from "./components/CommentComposer.vue";
import CommentItem from "./components/CommentItem.vue";
import DeleteCommentModal from "./components/DeleteCommentModal.vue";
import { internalCommentsCardPt } from "./styles";
import type { InternalCommentsProps, TodoComment } from "./types";

const props = withDefaults(defineProps<InternalCommentsProps>(), {
  applicationId: undefined,
  todoId: undefined,
});
const { applicationId, todoId } = toRefs(props);

const store = useStore();
const toast = useToast();

const { comments, isLoading } = useTodoComments(applicationId, todoId);
const { mutateAsync: deleteComment, isPending: isDeleting } = useDeleteTodoComment();

const commentPendingDelete = ref<TodoComment | undefined>();

const currentUserId = computed<string | undefined>(() => store.getters.getCurrentUser?.participantId);
const canCreate = computed<boolean>(
  () => hasTodoUpdateSubmissionPermission.value && !!applicationId.value && !!todoId.value,
);

const canManage = (comment: TodoComment): boolean =>
  hasTodoUpdateSubmissionPermission.value && !!currentUserId.value && comment.author_user_id === currentUserId.value;

const requestDelete = (comment: TodoComment): void => {
  commentPendingDelete.value = comment;
};

const cancelDelete = (): void => {
  commentPendingDelete.value = undefined;
};

const confirmDelete = async (): Promise<void> => {
  const comment = commentPendingDelete.value;
  try {
    if (comment) {
      await deleteComment({ applicationId: comment.application_id, todoId: comment.todo_id, commentId: comment.id });
    }
    commentPendingDelete.value = undefined;
  } catch {
    toast.open({
      message: "Error while deleting the comment. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <Card :pt="internalCommentsCardPt" data-testid="internal-comments-card">
    <template #header>
      <span class="text-base font-bold text-indigo-base">Internal Comments</span>
      <span
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-11p font-bold tracking-[0.3px] text-purple-800"
        data-testid="internal-comments-badge"
      >
        <LockIcon class="w-3 h-3" />
        Internal only
      </span>
    </template>
    <template #content>
      <p class="mt-3 text-12.5p font-medium leading-normal text-gray-600" data-testid="internal-comments-note">
        Notes and files added here relate to this to-do as a whole. They're visible to advisors only and are never
        shown to the student. Please don't include sensitive student data in these comments.
      </p>

      <CommentComposer
        v-if="canCreate && applicationId && todoId"
        :application-id="applicationId"
        :todo-id="todoId"
      />
      <div
        v-else
        class="flex items-start gap-2 mt-3 px-3.5 py-3 rounded-lg border border-gray-175 bg-gray-125 text-13p font-medium leading-normal text-gray-800"
        data-testid="internal-comments-read-only-note"
      >
        <LockIcon class="w-4 h-4 shrink-0 mt-px" />
        You have view-only access to internal comments.
      </div>

      <template v-if="!isLoading">
        <div
          v-if="!comments.length"
          class="mt-4 p-[18px] rounded-lg border border-dashed border-gray-175 text-center text-13p font-medium text-gray-500"
          data-testid="internal-comments-empty"
        >
          No internal comments yet.
        </div>
        <div v-else class="flex flex-col gap-4 mt-5" data-testid="internal-comments-list">
          <CommentItem
            v-for="comment in comments"
            :key="comment.id"
            :comment="comment"
            :can-manage="canManage(comment)"
            @delete="requestDelete"
          />
        </div>
      </template>

      <DeleteCommentModal
        :visible="!!commentPendingDelete"
        :is-deleting="isDeleting"
        @cancel="cancelDelete"
        @confirm="confirmDelete"
      />
    </template>
  </Card>
</template>
